import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import AnimatedSection from "./AnimatedSection";

interface Testimonial {
  id: string;
  author_name: string;
  author_role: string | null;
  content: string;
  rating: number;
  source: string | null;
}

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data } = await supabase
        .from("testimonials")
        .select("*")
        .eq("published", true)
        .order("display_order", { ascending: true });
      setTestimonials((data as Testimonial[]) ?? []);
      setLoading(false);
    };
    fetchTestimonials();
  }, []);

  if (loading || testimonials.length === 0) {
    return null;
  }

  return (
    <section className="py-24 md:py-32 bg-secondary/30">
      <div className="container">
        <AnimatedSection>
          <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-4 text-center">
            Témoignages
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-light mb-4 text-center">
            Ce que disent mes clients
          </h2>
          <p className="text-muted-foreground font-body text-center max-w-xl mx-auto mb-16">
            Des projets réalisés avec passion et un accompagnement personnalisé
          </p>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <AnimatedSection key={testimonial.id} delay={index * 0.1}>
              <motion.div
                className="bg-card border border-border rounded-sm p-6 h-full flex flex-col"
                whileHover={{ y: -4 }}
                transition={{ duration: 0.3 }}
              >
                <Quote className="h-8 w-8 text-accent/30 mb-4" />

                <p className="font-body text-sm leading-relaxed flex-1 mb-6">
                  "{testimonial.content}"
                </p>

                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`h-4 w-4 ${
                        i < testimonial.rating
                          ? "text-accent fill-accent"
                          : "text-muted-foreground/30"
                      }`}
                    />
                  ))}
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-heading text-base">{testimonial.author_name}</p>
                    {testimonial.author_role && (
                      <p className="text-xs font-body text-muted-foreground">
                        {testimonial.author_role}
                      </p>
                    )}
                  </div>
                  {testimonial.source && (
                    <span className="text-xs font-body text-muted-foreground/60 uppercase tracking-wider">
                      {testimonial.source}
                    </span>
                  )}
                </div>
              </motion.div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
